import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, Typography, TextField, Button, Box, Alert ,MenuItem
} from '@mui/material';
import api from '../services/api';
import PhoneNumberField from '../components/PhoneNumberField';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    age: '',
    sex: '',
    user_phone: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();

  // Handle change for regular text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field if it's been touched
    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  // Handle phone number change (from custom component)
  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      user_phone: value
    }));
    
    if (touched.user_phone) {
      setErrors(prev => ({
        ...prev,
        user_phone: validatePhone(value)
      }));
    }
  };

  // Field validation
  const validateField = (field, value) => {
    switch (field) {
      case 'username':
        return validateUsername(value);
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value);
      case 'user_phone':
        return validatePhone(value);
      case 'age':
        return validateAge(value);
      case 'sex':
        return validateSex(value);
      default:
        return '';
    }
  };

  const validateAge = (age) => {
    if (!age) return 'العمر مطلوب';
    if (age && (age < 1 || age > 120)) return 'الرجاء إدخال عمر صالح';
    return '';
  };

  const validateSex = (sex) => {
    if (sex === "") return 'نوع الجنس مطلوب';
    return '';
  };

  const validateUsername = (username) => {
    if (!username) return 'إسم المستخدم مطلوب';
    if (username.length < 3) return 'اسم المستخدم يجب أن يتكون من 3 أحرف على الأقل';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return 'يجب أن يحتوي اسم المستخدم فقط على أحرف وأرقام وشرطات سفلية';
    }
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'الإيميل مطلوب';
    if (!regex.test(email)) return 'يرجى إدخال عنوان بريد إلكتروني صالح';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'كلمة المرور مطلوبة';
    if (password.length < 6) return 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return 'يجب أن تحتوي كلمة المرور على أحرف كبيرة وصغيرة وأرقام';
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) return 'الرجاء تأكيد كلمة المرور الخاصة بك';
    if (confirmPassword !== formData.password) return 'كلمة المرور غير متطابقة';
    return '';
  };

  const validatePhone = (phone) => {
    const regex = /^(\+20|0)?1[0-9]{9}$/;
    if (!phone) return 'رقم الهاتف مطلوب';
    if (!regex.test(phone.replace(/\s/g, ''))) {
      return 'الرجاء إدخال رقم هاتف مصري صحيح (على سبيل المثال، 01234567890 أو +201234567890)';
    }
    return '';
  };

  // Handle blur event
  const handleBlur = (field) => (e) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, formData[field])
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newTouched = {};
    const newErrors = {};
    
    Object.keys(formData).forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
      return;
    }
    
    try {
      await api.post('/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        user_phone: formData.user_phone,
        age: formData.age,
        sex: formData.sex
      });
      
      navigate('/login', { 
        state: { 
          message: 'تم التسجيل بنجاح, الرجاء تسجيل الدخول' 
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.response?.data?.message || 'فشل التسجيل' 
      });
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return Object.values(errors).every(error => !error) && 
           Object.keys(touched).length === Object.keys(formData).length;
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4, mb: 10 }}>
      <Typography variant="h4" gutterBottom align="center">
        إنشاء حساب
      </Typography>
      
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.submit}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        <TextField
          fullWidth
          margin="normal"
          label="إسم المستخدم"
          name="username"
          value={formData.username}
          onChange={handleChange}
          onBlur={handleBlur('username')}
          error={touched.username && !!errors.username}
          helperText={touched.username && errors.username}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="الإيميل"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur('email')}
          error={touched.email && !!errors.email}
          helperText={touched.email && errors.email}
          required
        />
        
       <TextField
          fullWidth
          margin="normal"
          label="العمر"
          type="number"
          name="age"
          value={formData.age}
          onChange={handleChange}
          onBlur={handleBlur('age')}
          error={touched.age && !!errors.age}
          helperText={touched.age && errors.age}
          inputProps={{ min: 1, max: 120 }}
          required
        />
        
        <TextField
          select
          fullWidth
          margin="normal"
          label="الجنس"
          name="sex"
          value={formData.sex}
          onChange={handleChange}
          onBlur={handleBlur('sex')}
          error={touched.sex && !!errors.sex}
          helperText={touched.sex && errors.sex}
          required
        >
          <MenuItem value="">إختر نوع الجنس</MenuItem>
          <MenuItem value="male">ذكر</MenuItem>
          <MenuItem value="female">انثى</MenuItem>
        </TextField>

        <PhoneNumberField
          fullWidth
          margin="normal"
          label="رقم الهاتف"
          name="user_phone"
          value={formData.user_phone}
          onChange={handlePhoneChange}
          onBlur={handleBlur('user_phone')}
          error={touched.user_phone && !!errors.user_phone}
          helperText={touched.user_phone && errors.user_phone}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="كلمة المرور"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          onBlur={handleBlur('password')}
          error={touched.password && !!errors.password}
          helperText={touched.password && errors.password}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="تأكيد كلمة المرور"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur('confirmPassword')}
          error={touched.confirmPassword && !!errors.confirmPassword}
          helperText={touched.confirmPassword && errors.confirmPassword}
          required
        />
        
        <Button 
          type="submit" 
          variant="contained" 
          fullWidth 
          sx={{ mt: 3, py: 1.5 }}
          disabled={!isFormValid()}
        >
          إنشاء الحساب
        </Button>
      </Box>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body1">
          هل لديك حساب بالفعل؟{' '}
          <Button 
            component={Link} 
            to="/login" 
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            سجل دخولك من هنا
          </Button>
        </Typography>
      </Box>
    </Container>
  );
};

export default RegisterPage;